import { _ as _defineProperty } from '../../unsorted/defineProperty.js';
import { _ as _objectWithoutProperties } from '../../unsorted/objectWithoutProperties.js';
import { m as merge } from '../../unsorted/index.js';
import { useForwardLocalDomRef } from '../../hooks/useForwardLocalDomRef.js';
import { inputResetStyles } from '../../utils/styleHelpers.js';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';
import '@emotion/react';
import { B as Box } from '../Box/Box.js';
import { I as Icon } from '../Icon/Icon.js';

var containerSx = {
  w: "base.spacing.x12",
  h: "base.spacing.x7",
  pos: "relative",
  d: "flex",
  alignItems: "center",
  justifyContent: "flexStart",
  "&:disabled + .toggleContainer": {
    boxShadow: "none"
  },
  "input:hover:not(:disabled) + .toggleContainer": {
    boxShadow: "base.shadow.300",
    backgroundColor: "base.color.translucent.emphasis.200"
  },
  "input:hover:not(:disabled) + .toggleContainer:before": {
    borderColor: "base.color.translucent.standard.800"
  },
  "input:hover:not(:disabled):not(checked) + .toggleContainer > .toggleHandle": {
    translate: "5px 0",
    boxShadow: "base.shadow.300"
  },
  // Checked state styles:
  "input:checked + .toggleContainer": {
    background: "base.color.status.success.bright",
    boxShadow: "none"
  },
  "input:checked": {
    "& + .toggleContainer::before": {
      borderColor: "base.color.translucent.standard.1000",
      borderWidth: "base.border.size.200"
    },
    "& + .toggleContainer::after": {
      content: '""'
    }
  },
  "input:checked:hover:not(:disabled) + .toggleContainer": {
    background: "base.color.status.success.bright",
    boxShadow: "base.shadow.200"
  },
  "input:checked:not(:disabled):hover": {
    "& + .toggleContainer::before": {
      borderColor: "base.color.translucent.standard.1000",
      borderWidth: "base.border.size.100"
    },
    "& + .toggleContainer::after": {
      top: "1px",
      left: "1px",
      w: "calc(100% - 2px)",
      h: "calc(100% - 2px)"
    }
  },
  "input:checked + .toggleContainer > .toggleHandle": {
    translate: "23px 0",
    bg: "base.color.fixed.black.1000"
  },
  "input:checked + .toggleContainer > .toggleHandle > .toggleHandle__tickIcon": {
    opacity: "1"
  },
  "input:hover:checked:not(:disabled) + .toggleContainer > .toggleHandle": {
    translate: "21px 0"
  }
};
var toggleContainerSx = {
  pos: "relative",
  pointerEvents: "none",
  w: "base.spacing.x12",
  h: "base.spacing.x7",
  borderRadius: "base.borderRadius.x12",
  bg: "base.color.translucent.emphasis.100",
  d: "flex",
  alignItems: "center",
  transitionDuration: "base.motion.normal.fast.cssDuration",
  transitionTimingFunction: "base.motion.normal.fast.cssEase",
  transitionProperty: "all",
  "&::before": {
    content: '""',
    pos: "absolute",
    top: "0",
    left: "0",
    w: "100%",
    h: "100%",
    brad: "base.borderRadius.x12",
    borderWidth: "base.border.size.100",
    borderStyle: "solid",
    borderColor: "base.color.translucent.standard.500"
  },
  "&::after": {
    pos: "absolute",
    top: "2px",
    left: "2px",
    w: "calc(100% - 4px)",
    h: "calc(100% - 4px)",
    brad: "base.borderRadius.x12",
    borderWidth: "base.border.size.100",
    borderStyle: "solid",
    borderColor: "base.color.neutral.1000"
  }
};
var inputCss = {
  position: "absolute",
  top: "0",
  left: "0",
  width: "100%",
  height: "100%"
};
var handleSx = {
  w: "20px",
  h: "20px",
  d: "flex",
  pos: "relative",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  backgroundColor: "base.color.translucent.standard.1000",
  boxShadow: "base.shadow.100",
  transitionDuration: "base.motion.normal.fast.cssDuration",
  transitionTimingFunction: "base.motion.normal.fast.cssEase",
  transitionProperty: "translate, box-shadow",
  pointerEvents: "none",
  translate: "3px 0"
};
var tickSx = {
  fill: "base.color.fixed.white.1000",
  opacity: "0"
};

var _excluded = ["sx", "testId", "name", "id", "children", "domRef", "className", "onChange", "checked", "value", "disabled", "textAlign", "validationStatus", "inputRef", "rc"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function Toggle(_ref) {
  var _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? "Toggle" : _ref$testId,
    name = _ref.name,
    _ref$id = _ref.id,
    id = _ref$id === void 0 ? name : _ref$id;
    _ref.children;
    var _ref$domRef = _ref.domRef,
    domRef = _ref$domRef === void 0 ? {
      current: null
    } : _ref$domRef,
    className = _ref.className,
    onChange = _ref.onChange,
    checked = _ref.checked,
    value = _ref.value,
    disabled = _ref.disabled;
    _ref.textAlign;
    _ref.validationStatus;
    var inputRef = _ref.inputRef,
    rc = _ref.rc,
    inputHtmlAttrs = _objectWithoutProperties(_ref, _excluded);
  var localDomRef = useForwardLocalDomRef(domRef);
  var resetStyles = inputResetStyles(disabled);
  return jsxs(Box, {
    rc: rc,
    domRef: localDomRef,
    testId: testId,
    className: "".concat(className !== null && className !== void 0 ? className : "", " Toggle"),
    sx: merge(containerSx, sx),
    children: [jsx("input", _objectSpread(_objectSpread({}, inputHtmlAttrs), {}, {
      type: "checkbox",
      "data-testid": "".concat(testId, "__input"),
      id: id,
      name: name,
      value: value,
      checked: checked,
      onChange: onChange,
      disabled: disabled,
      ref: inputRef,
      css: merge(resetStyles, inputCss),
      className: "actualInput"
    })), jsx(Box, {
      className: "toggleContainer",
      sx: toggleContainerSx,
      testId: "".concat(testId, "__toggleContainer"),
      children: jsx(Box, {
        className: "toggleHandle",
        rc: jsx("span", {}),
        sx: handleSx,
        testId: "".concat(testId, "__toggleContainer__toggleHandle"),
        children: jsx(Icon, {
          icon: "Tick",
          variant: disabled ? "regular" : "bold",
          testId: "".concat(testId, "__checkIcon"),
          sx: tickSx,
          className: "toggleHandle__tickIcon"
        })
      })
    })]
  });
}
Toggle.displayName = "Toggle";

export { Toggle as T };
//# sourceMappingURL=Toggle.js.map
